// A tab left open moves to a new deploy by itself (owner 2026-10-29): the build's stamp in /version.json
// is checked every few minutes and whenever the tab is hidden; once it changed, the page reloads at the
// first moment nobody is looking (tab hidden, Zzz or Przerwa) with nothing under way — no request on
// the way, no unsent farm work, no ride or drive, no match, no panel open. Just before, her spot is
// saved (her scene comes back from it, BootScene) and a visit is remembered for BootScene to resume —
// as it is on every refresh (owner 2026-10-29: refreshing at a friend's took her home).
import { ASSET_VERSION } from '../assetVersion.js';
import { SCENE, VISIT_SCENES, RESTORABLE_SCENES } from '../config.js';
import { requestsInFlight } from '../api/client.js';
import { matchClient } from '../api/matchClient.js';
import { gameState } from '../state/gameState.js';
import { visitState } from '../state/visitState.js';
import { matchHoldsInactive } from '../state/inactivityEffects.mjs';
import { isNewVersion, reloadAllowed, visitSnapshot, SNAPSHOT_KEY, TRIED_KEY } from '../state/autoUpdate.mjs';
import { bikeSession } from './bikeSession.js';
import { isInputLocked } from './inputLock.js';
import { topScene } from './SettingsModal.js';

const CHECK_MS = 5 * 60_000;
const TICK_MS = 15_000;                 // while one is waiting: is it a good moment yet?
const NEVER_RELOAD_IN = new Set([SCENE.BOOT, SCENE.TRAINING_ROUTE, SCENE.BOARD_GAME]);   // loading, a driving lesson / exam, a board game

const session = () => { try { return globalThis.sessionStorage ?? null; } catch { return null; } };

// A visit lives only in this page (the server saves no position there): note it for BootScene.
function rememberVisit(game) {
  const top = topScene(game), key = top?.scene.key, n = top?.natalka;
  if (!VISIT_SCENES.has(key)) return;
  const at = top?.furnitureSeats?.standingPoint() ?? n;   // seated, (x, y) is the pose anchor on the piece
  const snap = visitSnapshot({ scene: key, hostId: visitState.hostId, hostName: visitState.hostName, x: at?.x, y: at?.y }, Date.now());
  try { if (snap) session()?.setItem(SNAPSHOT_KEY, snap); } catch { /* lands at home instead */ }
}

export function installAutoUpdate(game, idleTracker) {
  window.addEventListener('pagehide', () => rememberVisit(game));   // a refresh, or the reload below
  if (ASSET_VERSION === 'dev') return;               // the dev server reloads by itself
  let pending = null, reloading = false;

  const check = async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      const remote = (await res.json())?.version ?? null;
      let tried = null;
      try { tried = session()?.getItem(TRIED_KEY) ?? null; } catch { /* blocked storage */ }
      if (isNewVersion(ASSET_VERSION, remote, tried)) pending = remote;
    } catch { /* offline, or no version.json yet: try again later */ }
  };

  const busy = () => {
    const scenes = game.scene.getScenes(true);
    return requestsInFlight() > 0 || bikeSession.riding || matchHoldsInactive(matchClient.info)
      || scenes.some((s) => NEVER_RELOAD_IN.has(s.scene.key) || isInputLocked(s) || s.reloadBlocked?.());
  };

  const reload = async () => {
    reloading = true;
    try { session()?.setItem(TRIED_KEY, pending); } catch { /* the reload still happens */ }
    const top = topScene(game), key = top?.scene.key, n = top?.natalka;
    if (RESTORABLE_SCENES.has(key) && n?.visible) {      // (a visit is noted on pagehide)
      // the throttled save can be a step behind (a hidden tab runs no update() to catch up)
      const facing = Number.isInteger(window.__myFacing) ? window.__myFacing : null;
      await gameState.setLocation(key, Math.round(n.x), Math.round(n.y), facing);
    }
    location.reload();
  };

  const maybeReload = () => {
    if (reloading || !pending) return;
    if (reloadAllowed({ pending, hidden: document.hidden, stage: idleTracker.stage, busy: busy() })) reload();
  };

  setInterval(() => check().then(maybeReload), CHECK_MS);
  setInterval(maybeReload, TICK_MS);
  document.addEventListener('visibilitychange', () => { check().then(maybeReload); });
  idleTracker.onStage(() => maybeReload());
}
